import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Paperclip,
  Printer,
  ReceiptText,
  Pause,
  Ban,
  ArrowLeftRight,
  X,
} from "lucide-react";
import { encountersApi, encounterQueryKeys } from "../../shared/api/encounters";
import { appointmentQueryKeys } from "../../shared/api/appointments";
import { billingApi } from "../../shared/api/billing";
import { prescriptionsApi } from "../../shared/api/prescriptions";
import { servicesApi, servicesQueryKeys } from "../../shared/api/services";
import { escapeHtml } from "../../shared/escapeHtml";
import { queueOpsApi } from "../../shared/api/queueOps";
import {
  symptomSuggestionsApi,
  symptomSuggestionQueryKeys,
} from "../../shared/api/symptomSuggestions";
import { apiError } from "../../shared/api/client";
import { Skeleton } from "../../shared/ui/Skeleton";
import { ErrorState } from "../../shared/ui/ErrorState";
import { useToast } from "../../shared/ui/Toast";
import { TemplatePickerModal } from "./components/TemplatePickerModal";
import { patientsApi } from "@/shared/api/patients";
import type {
  AllergyOverrideReason,
  DrugAllergyWarning,
  AllergySeverity,
  EncounterAllergyConflict,
} from "@/shared/types/patient";
import { allergySeverityLabel } from "@/shared/format";
import { TransferDepartmentDialog } from "./components/TransferDepartmentDialog";
import {
  DrugAllergyStrip,
  DrugAllergyWarningDialog,
} from "./components/DrugAllergyGuard";
import { FollowUpPanel, type FollowUpPanelHandle } from "./components/FollowUpPanel";
import { VitalsPanel } from "./components/VitalsPanel";
import { QueueActions } from "./components/QueueActions";
import { PrescriptionSection } from "./components/PrescriptionSection";
import { useAppointmentActionGuard } from "./hooks/useAppointmentActionGuard";
import { useOperatesModule } from "@/shared/hooks/useOperatesModule";
import { EncounterQueueSidebar } from "./components/EncounterQueueSidebar";
import { PatientContextPanel } from "./components/PatientContextPanel";
import { DiagnosisPanel } from "./components/DiagnosisPanel";
import { OrdersPanel } from "./components/OrdersPanel";
import { DentalChartPanel } from "../patients/components/DentalChartPanel";
import { dentalApi, dentalQueryKeys } from "../../shared/api/dental";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import type { PrescriptionTemplateResponse } from "../../shared/types/prescriptionTemplate";
import { SOAP_DEFAULTS } from "../../shared/types/encounter";
import { randomUuid } from "../../shared/randomUuid";
import type {
  Diagnosis,
  EncounterPrescriptionItem,
  SoapNote,
  Vitals,
} from "../../shared/types/encounter";

function genderLabel(g: string | null): string {
  return g === "M" ? "Nam" : g === "F" ? "Nữ" : g ? "Khác" : "—";
}

/** Human label for the booking source (who created the appointment). */
function bookingSourceLabel(src: string | null): string {
  switch (src) {
    case "PATIENT":
      return "Bệnh nhân tự đặt";
    case "STAFF":
      return "Lễ tân đặt";
    case "AI_ASSISTANT":
      return "Trợ lý AI";
    default:
      return "—";
  }
}

/** Date-only formatter (dd/MM/yyyy, VN timezone) for the exam-history list. */
function formatDateVn(iso: string): string {
  return new Date(iso).toLocaleDateString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Local YYYY-MM-DD for "hôm nay + n ngày" — default for the next-visit date input. */
function isoPlusDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/** YYYY-MM-DD → DD/MM/YYYY (no timezone parsing). */
function isoToVn(iso: string): string {
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
}

/** Shared Tailwind class for the plain native selects still used inline. */

/**
 * Quick-pick phrase banks — one-click chips that append a common phrase into an
 * otherwise free-text (and still editable) field. Built for the 5-min consult:
 * the doctor clicks instead of typing, then tweaks only if needed.
 */
const PICKS = {
  advice: ["Uống thuốc đúng đơn", "Tái khám sau 3 ngày", "Tái khám sau 5 ngày", "Tái khám khi có bất thường", "Uống nhiều nước", "Nghỉ ngơi, giữ ấm", "Ăn uống điều độ"],
} as const;

/** Common Vietnamese symptom suggestions for the "Bệnh nhân tự mô tả" chip input. */
const SYMPTOMS = [
  "Sốt", "Ho", "Đau họng", "Sổ mũi", "Nghẹt mũi", "Đau đầu", "Chóng mặt",
  "Đau bụng", "Buồn nôn", "Nôn", "Tiêu chảy", "Táo bón", "Đau lưng", "Đau khớp",
  "Mệt mỏi", "Khó thở", "Đau ngực", "Hồi hộp", "Mất ngủ", "Chán ăn", "Phát ban",
  "Ngứa", "Ù tai", "Đau mắt", "Ớn lạnh", "Đổ mồ hôi", "Sụt cân", "Khó nuốt",
  "Tê bì", "Phù",
];

/**
 * Legacy read only (#18839). Chỉ định CLS/dịch vụ lived inside `assessment.supplementaryLabUs` as
 * "Chỉ định: a; b" until #18805 gave them their own records. They are no longer written there: the
 * field's own label is "Kết quả XN, siêu âm bổ sung", the notes screen edits it as free text, and
 * `updateSoap` sends the whole note — so typing results erased the orders and adding an order
 * erased the results, both silently.
 *
 * <p>Still parsed for an encounter raised before the structured table. The prefix check is what
 * makes that safe: a doctor's typed results do not start with it, so a note holding results reads
 * as "no orders" rather than as one order named after the findings.
 */
const SVC_PREFIX = "Chỉ định: ";
function parseOrders(note: string): string[] {
  if (!note || !note.startsWith(SVC_PREFIX)) return [];
  return note
    .slice(SVC_PREFIX.length)
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Append a phrase to a field value, skipping exact duplicates. */
function appendPhrase(current: string, phrase: string, sep: string): string {
  const parts = current
    .split(sep === "\n" ? /\n/ : new RegExp(`\\s*${sep.trim()}\\s*`))
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.includes(phrase)) return current;
  return current.trim() ? current.replace(/\s+$/, "") + sep + phrase : phrase;
}



/** Quick-pick chip row: click to append a phrase into the field above. */
function QuickPicks({
  picks,
  onPick,
  disabled,
}: {
  picks: readonly string[];
  onPick: (phrase: string) => void;
  disabled?: boolean;
}) {
  if (disabled) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-muted-foreground">Chọn nhanh:</span>
      {picks.map((p) => (
        <button
          key={p}
          type="button"
          className="rounded-md border border-input bg-background px-2 py-1 text-xs text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          onClick={() => onPick(p)}
        >
          + {p}
        </button>
      ))}
    </div>
  );
}

/**
 * Gmail-"To"-style chip input: type to filter suggestions, Enter or click adds a
 * chip, × removes it. Free text + Enter also becomes a chip. Chips are emitted as
 * a de-duplicated string array to the parent.
 */
function ChipSuggestInput({
  chips,
  onChange,
  suggestions,
  disabled,
  placeholder,
}: {
  chips: string[];
  onChange: (next: string[]) => void;
  suggestions: readonly string[];
  disabled?: boolean;
  placeholder?: string;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  function addChip(raw: string) {
    const v = raw.trim();
    if (!v) return;
    if (!chips.some((c) => c.toLowerCase() === v.toLowerCase())) {
      onChange([...chips, v]);
    }
    setQ("");
    setOpen(false);
  }
  function removeChip(i: number) {
    onChange(chips.filter((_, x) => x !== i));
  }

  const filtered = suggestions.filter(
    (s) =>
      s.toLowerCase().includes(q.trim().toLowerCase()) &&
      !chips.some((c) => c.toLowerCase() === s.toLowerCase())
  );

  return (
    <div className="relative">
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background">
        {chips.map((c, i) => (
          <span
            key={`${c}-${i}`}
            className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs text-secondary-foreground"
          >
            {c}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeChip(i)}
                aria-label={`Bỏ ${c}`}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <input
            className="min-w-[8rem] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            value={q}
            onChange={(ev) => {
              setQ(ev.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 120)}
            onKeyDown={(ev) => {
              if (ev.key === "Enter") {
                ev.preventDefault();
                addChip(q);
              } else if (ev.key === "Backspace" && q === "" && chips.length) {
                removeChip(chips.length - 1);
              }
            }}
            placeholder={chips.length === 0 ? placeholder : ""}
          />
        )}
      </div>
      {open && !disabled && q.trim() !== "" && filtered.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-auto rounded-md border bg-popover p-1 shadow-md">
          {filtered.slice(0, 8).map((s) => (
            <button
              key={s}
              type="button"
              className="block w-full rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
              onMouseDown={(ev) => ev.preventDefault()}
              onClick={() => addChip(s)}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Client-side single-service referral slip ("PHIẾU CHỈ ĐỊNH"). Opens a new window
 * with a paper-friendly (A5-ish) layout and triggers print — no backend needed.
 */
function printServiceSlip(opts: {
  serviceName: string;
  patientName: string;
  patientCode: string | null;
  patientAge: number | null;
  patientGender: string | null;
  doctorName: string;
}) {
  const today = new Date().toLocaleDateString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const html = `<!doctype html><html lang="vi"><head><meta charset="utf-8">
<title>Phiếu chỉ định</title>
<style>
  @page { size: A5; margin: 12mm; }
  * { font-family: "Times New Roman", serif; }
  body { color: #000; font-size: 13px; }
  h1 { text-align: center; font-size: 16px; margin: 0 0 4px; text-transform: uppercase; }
  .sub { text-align: center; font-size: 12px; margin: 0 0 16px; }
  .row { margin: 4px 0; }
  .lbl { font-weight: bold; }
  .svc { margin: 16px 0; padding: 10px 12px; border: 1px solid #000; font-size: 15px; font-weight: bold; }
  .sign { margin-top: 40px; text-align: right; }
  .sign .role { font-weight: bold; }
  .sign .hint { font-style: italic; font-size: 11px; }
</style></head><body>
  <h1>Phiếu chỉ định cận lâm sàng / dịch vụ</h1>
  <p class="sub">Ngày ${escapeHtml(today)}</p>
  <div class="row"><span class="lbl">Họ tên:</span> ${escapeHtml(opts.patientName)}</div>
  <div class="row">
    <span class="lbl">Mã BN:</span> ${escapeHtml(opts.patientCode ?? "—")}
    &nbsp;&nbsp; <span class="lbl">Tuổi:</span> ${opts.patientAge ?? "—"}
    &nbsp;&nbsp; <span class="lbl">Giới:</span> ${escapeHtml(genderLabel(opts.patientGender))}
  </div>
  <div class="svc">Chỉ định: ${escapeHtml(opts.serviceName)}</div>
  <div class="sign">
    <div>Ngày ${escapeHtml(today)}</div>
    <div class="role">Bác sĩ chỉ định</div>
    <div class="hint">(Ký, ghi rõ họ tên)</div>
    <div style="margin-top:34px">${escapeHtml(opts.doctorName)}</div>
  </div>
  <script>window.onload=function(){window.print();};</script>
</body></html>`;
  const w = window.open("", "_blank", "width=600,height=800");
  if (!w) return;
  w.document.write(html);
  w.document.close();
}

/** Section wrapper — a titled card block used throughout the exam column. */
function ExamSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Card className="p-4">
      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {children}
    </Card>
  );
}

/**
 * Single-screen Khám bệnh workspace (T30+). Left = doctor queue, centre = exam
 * (lý do / hỏi bệnh / khám lâm sàng), right = chẩn đoán + kê đơn + tổng kết,
 * bottom = action bar with F9 = Hoàn tất khám. Everything auto-saves; the
 * dedicated /encounters/:id/* pages remain as deep editors.
 */
export function EncounterWorkspacePage() {
  const { id: rawId } = useParams<{ id: string }>();
  const id = rawId ?? "";
  const qc = useQueryClient();
  const navigate = useNavigate();

  const detail = useQuery({
    queryKey: encounterQueryKeys.detail(id),
    queryFn: () => encountersApi.get(id),
    enabled: !!rawId,
  });
  /** Chỉ định CLS/dịch vụ as data — the source of truth since #18805/#18839. */
  const svcOrderQuery = useQuery({
    queryKey: encounterQueryKeys.serviceOrders(id),
    queryFn: () => encountersApi.listServiceOrders(id),
    enabled: !!rawId,
  });
  // Chip triệu chứng gợi ý — nạp từ danh mục backend, fallback về SYMPTOMS khi
  // đang tải hoặc danh mục trống để chip không bao giờ rỗng.
  const symptomOptions = useQuery({
    queryKey: symptomSuggestionQueryKeys.options(),
    queryFn: () => symptomSuggestionsApi.options(),
  });
  const symptomSuggestions = useMemo(() => {
    const labels = (symptomOptions.data ?? [])
      .map((o) => o.label)
      .filter(Boolean);
    return labels.length ? labels : SYMPTOMS;
  }, [symptomOptions.data]);
  const today = useMemo(
    () =>
      new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Ho_Chi_Minh",
      }).format(new Date()),
    []
  );
  // LỊCH SỬ KHÁM — previous encounters for this patient (excludes current).
  const patientId = detail.data?.patientId;
  const history = useQuery({
    queryKey: ["enc-history", patientId],
    queryFn: () =>
      encountersApi
        .list({ patientId }, { page: 0, size: 20, sort: "startedAt,desc" })
        .then((r) => r.content),
    enabled: !!patientId,
  });

  // ---- local drafts (initialised once per encounter id) ----
  const initId = useRef<string | null>(null);
  const [chief, setChief] = useState("");
  const [soap, setSoap] = useState<SoapNote | null>(null);
  const [vitals, setVitals] = useState<Vitals | null>(null);
  const [rx, setRx] = useState<EncounterPrescriptionItem[]>([]);
  const [dx, setDx] = useState<Diagnosis[]>([]);
  const [err, setErr] = useState<string | null>(null);
  // #18526 — the "need a diagnosis" error belongs at the Chẩn đoán section, not
  // the general error area near Kê đơn. Flag + ref to highlight/scroll there.
  const [dxError, setDxError] = useState(false);
  const dxSectionRef = useRef<HTMLDivElement>(null);
  // #18535 — a prescribed drug with quantity <= 0 must block completion (sign +
  // auto-invoice + print) BEFORE the encounter is signed/locked, not fail silently
  // on the swallowed prescription POST. Flag (offending drug name) + ref to the Rx
  // section so we can highlight/scroll like the diagnosis guard.
  const [rxError, setRxError] = useState<string | null>(null);
  const rxSectionRef = useRef<HTMLDivElement>(null);
  const [savedToast, setSavedToast] = useState(false);
  // Thông báo tạo lịch tái khám (thành công) + cảnh báo mềm (thất bại, không chặn ký).
  const [tplOpen, setTplOpen] = useState(false);
  // Chỉ định CLS/dịch vụ (X-quang, siêu âm…) — persisted into the paraclinical note.
  const [svcOrders, setSvcOrders] = useState<string[]>([]);
  // Per-rx-item UI state: chosen duration (days) + whether the manual dose grid is shown.
  // Ngày hẹn kế tiếp (tái khám) — YYYY-MM-DD; persisted as a "Hẹn tái khám: …" line in lời dặn.
  const [followUp, setFollowUp] = useState("");
  // Khoa/Phòng/Bác sĩ/Dịch vụ cho lịch tái khám, theo booking-policy dimensions. Bỏ trống →
  // backend kế thừa từ lần khám (Khoa/Bác sĩ) hoặc để trống (Phòng/Dịch vụ).
  // TD-7b (#18800): Khoa/Phòng/Bác sĩ/Dịch vụ + their lookups + the ok/warning lines moved into
  // FollowUpPanel. Only the DATE is still here, because choosing it also rewrites the
  // "Hẹn tái khám:" line inside the SOAP plan — encounter state this page owns.
  const followUpRef = useRef<FollowUpPanelHandle>(null);

  // Sơ đồ răng ngay trong lượt khám (#18868). Cùng cách gate như tab ở hồ sơ bệnh nhân, nên
  // phòng khám không phải nha khoa đơn giản là không thấy mục này.
  const dentalCap = useQuery({
    queryKey: dentalQueryKeys.capability(),
    queryFn: () => dentalApi.capability(),
    staleTime: 5 * 60_000,
  });
  const dentalEnabled = dentalCap.data?.enabled ?? false;
  // Khám bệnh là màn hình gói nào cũng có (ADR-0053), nhưng mục "Kê đơn" gọi vào vùng Đơn thuốc —
  // phòng khám không kê đơn trên Addy (ADR-0048), hay gói không có Đơn thuốc, thì mục này không
  // có gì để làm và mọi lượt gọi của nó nhận 409/402. Lạc quan (`operates`, không chờ `loaded`):
  // vẽ mục này không tốn request nào — ô chọn thuốc chỉ gọi khi gõ, còn tồn kho tự gate riêng.
  const prescribing = useOperatesModule("PRESCRIPTIONS");
  // Chuyển khoa (department transfer) — chỉ mở được sau khi đã ký lần khám.
  // TD-7b (#18800): the target department, reason and success toast moved INTO
  // TransferDepartmentDialog, which is the only thing that ever read them. Only the open flag is
  // still here, because the button that opens it is in the workspace toolbar.
  const [transferOpen, setTransferOpen] = useState(false);
  /** #18859 — a picked medicine held back while the doctor answers the allergy warning. */
  const [allergyPrompt, setAllergyPrompt] = useState<{
    med: { id: string; name: string; unit: string; strength: string | null };
    warnings: DrugAllergyWarning[];
  } | null>(null);

  useEffect(() => {
    const d = detail.data;
    // Waits for the orders query to settle as well: seeding from the note first and never
    // re-running would show a legacy sentence in place of the real orders (#18839).
    if (d && !svcOrderQuery.isLoading && initId.current !== d.id) {
      initId.current = d.id;
      const initialChief = d.chiefComplaint ?? d.soap.history.chiefComplaint ?? "";
      // First load with no chief yet: seed chips from the patient's self-description.
      setChief(
        initialChief.trim() === "" && d.patientSelfDescription
          ? d.patientSelfDescription
              .split(/[,;]/)
              .map((s) => s.trim())
              .filter(Boolean)
              .join(", ")
          : initialChief
      );
      setSoap(d.soap);
      setVitals(d.vitals);
      setRx(d.prescription.items);
      setDx(d.diagnoses);
      // Orders come from their own records; the note is read only when there are none, i.e. this
      // encounter predates them (#18839).
      setSvcOrders(
        (svcOrderQuery.data ?? []).length > 0
          ? (svcOrderQuery.data ?? []).map((o) => o.serviceName)
          : parseOrders(d.soap.assessment.supplementaryLabUs)
      );
      // Ngày hẹn kế tiếp: đọc lại từ dòng "Hẹn tái khám: dd/mm/yyyy" nếu có, mặc định +7 ngày.
      const fu = (d.soap.assessment.definitivePlan || "").match(
        /Hẹn tái khám:\s*(\d{2})\/(\d{2})\/(\d{4})/
      );
      setFollowUp(fu ? `${fu[3]}-${fu[2]}-${fu[1]}` : isoPlusDays(7));
    }
  }, [detail.data, svcOrderQuery.isLoading, svcOrderQuery.data]);

  const readOnly = !!detail.data && detail.data.status !== "IN_PROGRESS";

  // ---- #19184: drug-allergy gate on signing the VISIT ----------------------------------------
  //
  // "Hoàn tất đơn" on the Đơn thuốc screen already refused an unanswered allergy (#18859), but the
  // three "Hoàn tất" buttons here sign the visit, print the summary and raise the medicine invoice
  // without that screen ever opening. The server now refuses too (422
  // encounter_allergy_override_required); this asks first, with the same dialog and the same
  // reasons, and records the answer on the visit — which also satisfies "Hoàn tất đơn", so the
  // doctor is never asked twice.
  const rxCatalogIds = useMemo(
    () =>
      [
        ...new Set(
          rx
            .map((r) => r.medicineId)
            .filter((m): m is string => Boolean(m) && !String(m).startsWith("drugdb:"))
        ),
      ].sort(),
    [rx]
  );
  const visitAllergy = useQuery({
    queryKey: ["encounter-allergy-conflicts", id, patientId, rxCatalogIds],
    queryFn: () => patientsApi.encounterAllergyConflicts(patientId!, id, rxCatalogIds),
    enabled: Boolean(patientId) && rxCatalogIds.length > 0,
  });
  const allergyMarks = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of visitAllergy.data ?? []) {
      const line = `${c.substance} · ${allergySeverityLabel[c.severity as AllergySeverity] ?? c.severity}`;
      const prev = m.get(c.medicineId);
      m.set(
        c.medicineId,
        (prev ? `${prev}; ${line}` : line) + (c.overridden ? " (đã xác nhận lý do)" : "")
      );
    }
    return m;
  }, [visitAllergy.data]);
  const [signGate, setSignGate] = useState<{
    queue: { medicineId: string; medicineName: string; warnings: DrugAllergyWarning[] }[];
    proceed: () => void;
  } | null>(null);

  async function withAllergyGate(proceed: () => void) {
    if (!patientId || rxCatalogIds.length === 0) {
      proceed();
      return;
    }
    let conflicts: EncounterAllergyConflict[];
    try {
      conflicts = (await visitAllergy.refetch()).data ?? [];
    } catch {
      // The pre-check is a courtesy; the server's refusal is the guarantee. Let it answer.
      proceed();
      return;
    }
    const open = conflicts.filter((c) => !c.overridden);
    if (open.length === 0) {
      proceed();
      return;
    }
    const byMed = new Map<
      string,
      { medicineId: string; medicineName: string; warnings: DrugAllergyWarning[] }
    >();
    for (const c of open) {
      const entry = byMed.get(c.medicineId) ?? {
        medicineId: c.medicineId,
        medicineName: c.medicineName,
        warnings: [],
      };
      entry.warnings.push({
        allergyId: c.allergyId,
        substance: c.substance,
        severity: c.severity,
        reaction: c.reaction,
        matchedOn: c.matchedOn,
        severe: c.severe,
        overridden: false,
      });
      byMed.set(c.medicineId, entry);
    }
    setSignGate({ queue: [...byMed.values()], proceed });
  }

  async function answerSignGate(reasonCode: AllergyOverrideReason, reasonText: string | null) {
    const gate = signGate;
    if (!gate || !patientId) return;
    const [head, ...rest] = gate.queue;
    try {
      // One row per matched allergy — the server checks each (medicine, allergy) pair.
      await Promise.all(
        head.warnings.map((w) =>
          patientsApi.overrideDrugAllergy(patientId, {
            medicineId: head.medicineId,
            allergyId: w.allergyId,
            encounterId: id,
            reasonCode,
            reasonText,
          })
        )
      );
    } catch (e2) {
      setSignGate(null);
      setErr(`Chưa ghi được xác nhận dị ứng: ${apiError(e2).detail}`);
      return;
    }
    if (rest.length > 0) {
      setSignGate({ queue: rest, proceed: gate.proceed });
      return;
    }
    setSignGate(null);
    await visitAllergy.refetch();
    gate.proceed();
  }

  // ---- debounced savers ----
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const debounce = (key: string, fn: () => void, delay = 700) => {
    clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(fn, delay);
  };
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: encounterQueryKeys.detail(id) });
  // Mark the cached detail stale WITHOUT refetching now: these three savers are debounced
  // free-text fields, so an immediate refetch would re-seed the local SOAP/chief/vitals state
  // mid-typing (see the [detail.data] effect) and clobber in-progress edits. refetchType:"none"
  // just invalidates the cache, so re-opening the encounter (after switching patients) fetches the
  // saved data instead of showing the stale copy until a browser refresh (#18598).
  const markDetailStale = () =>
    qc.invalidateQueries({
      queryKey: encounterQueryKeys.detail(id),
      refetchType: "none",
    });

  const saveChief = useMutation({
    mutationFn: (v: string) => encountersApi.updateChiefComplaint(id, v || null),
    onSuccess: markDetailStale,
  });
  const saveSoap = useMutation({
    mutationFn: (s: SoapNote) =>
      encountersApi.updateSoap(id, {
        history: s.history,
        exam: s.exam,
        assessment: s.assessment,
      }),
    onSuccess: markDetailStale,
  });
  const saveVitals = useMutation({
    mutationFn: (v: Vitals) => encountersApi.updateVitals(id, v),
    onSuccess: markDetailStale,
  });
  const saveRx = useMutation({
    mutationFn: (items: EncounterPrescriptionItem[]) =>
      encountersApi.replacePrescription(
        id,
        items.map(({ id: _drop, ...rest }) => rest)
      ),
    onSuccess: invalidate,
    onError: (e2) => setErr(apiError(e2).detail),
  });
  const saveDx = useMutation({
    mutationFn: (list: Diagnosis[]) =>
      encountersApi.replaceDiagnoses(
        id,
        list.map(({ id: _drop, ...rest }) => rest)
      ),
    onSuccess: invalidate,
    onError: (e2) => setErr(apiError(e2).detail),
  });

  // ---- mutators ----
  function patchSoap(section: keyof SoapNote, field: string, value: string) {
    setSoap((prev) => {
      if (!prev) return prev;
      const next = {
        ...prev,
        [section]: { ...(prev[section] as object), [field]: value },
      } as SoapNote;
      debounce("soap", () => saveSoap.mutate(next));
      return next;
    });
  }
  function patchVital(field: keyof Vitals, value: number | null) {
    setVitals((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [field]: value } as Vitals;
      debounce("vitals", () => saveVitals.mutate(next));
      return next;
    });
  }
  type PickedMed = { id: string; name: string; unit: string; strength: string | null };

  /**
   * #18859 — check the picked medicine against the patient's recorded drug allergies BEFORE it
   * becomes a prescription line. A match stops and asks; anything else adds the line as before.
   *
   * <p>The check is best-effort: if it fails (offline, permission, server error) the medicine is
   * added anyway. A safety check that can block prescribing when it is itself broken is a worse
   * failure than the one it prevents.
   */
  async function pickMed(m: PickedMed) {
    if (!patientId) {
      addMed(m);
      return;
    }
    try {
      const warnings = await patientsApi.checkDrugAllergy(patientId, m.id, id);
      // Already answered on this visit → do not ask again (#18859 §9). The override stands and
      // the backend will accept the finalize; asking twice is how warnings become noise.
      const unanswered = warnings.filter((w) => !w.overridden);
      if (unanswered.length > 0) {
        setAllergyPrompt({ med: m, warnings: unanswered });
        return;
      }
    } catch {
      // fall through — see the note above
    }
    addMed(m);
  }

  function addMed(m: {
    id: string;
    name: string;
    unit: string;
    strength: string | null;
  }) {
    const next: EncounterPrescriptionItem[] = [
      ...rx,
      {
        id: randomUuid(),
        medicineId: m.id,
        medicineName: m.name,
        medicineUnit: m.unit,
        medicineStrength: m.strength,
        quantity: 0,
        doseMorning: 0,
        doseNoon: 0,
        doseAfternoon: 0,
        doseEvening: 0,
        doseUnit: m.unit,
        route: "ORAL",
        mealTiming: "AFTER_MEAL",
        note: null,
      },
    ];
    setRx(next);
    saveRx.mutate(next);
  }
  function patchRx(i: number, patch: Partial<EncounterPrescriptionItem>) {
    setRx((prev) => {
      const next = prev.map((it, x) => (x === i ? { ...it, ...patch } : it));
      debounce("rx", () => saveRx.mutate(next));
      return next;
    });
  }
  function removeRx(i: number) {
    const next = rx.filter((_, x) => x !== i);
    setRx(next);
    saveRx.mutate(next);
  }

  // ---- chẩn đoán (ICD-10) ----
  // ---- inline medicine search ----


  const services = useQuery({
    queryKey: servicesQueryKeys.all(),
    queryFn: () => servicesApi.listAll(),
  });

  // Booking-policy dimensions cho lịch tái khám (hiện/ẩn/bắt buộc + thứ tự Khoa/Phòng/BS/DV).

  // ---- chỉ định dịch vụ / CLS (persist into the paraclinical note) ----
  function setServiceOrders(next: string[]) {
    setSvcOrders(next);
    // Written ONLY as data now (#18839). The note is no longer touched, so the doctor's
    // "Kết quả XN, siêu âm bổ sung" text survives an order being added or removed.
    //
    // One exception, and it is a migration rather than a write: if the note still holds the
    // machine-written "Chỉ định: …" sentence, clear it. Otherwise a legacy encounter whose orders
    // are all REMOVED would fall back to that stale sentence — the backend reads the note when
    // there are no structured rows — and bill services the doctor just deleted. The prefix check
    // means a note holding real clinical text is never touched.
    if (soap?.assessment.supplementaryLabUs?.startsWith(SVC_PREFIX)) {
      patchSoap("assessment", "supplementaryLabUs", SOAP_DEFAULTS.supplementaryLabUs);
    }
    const catalogue = services.data ?? [];
    encountersApi
      .replaceServiceOrders(
        id,
        next.map((name) => ({
          serviceId: catalogue.find((s) => s.name === name)?.id ?? null,
          serviceName: name,
        })),
      )
      .then(() => qc.invalidateQueries({ queryKey: encounterQueryKeys.serviceOrders(id) }))
      // Soft-fail: the doctor keeps working and the list stays on screen. It is no longer backed
      // by the note, so a failure here loses the order — surfaced rather than swallowed would be
      // better, and that is #18841.
      .catch(() => {});
  }

  // ---- rx dosing helpers (cách dùng dropdown + toa mẫu) ----
  // Save an rx change immediately (discrete choices) rather than debounced.
  function setRxImmediate(i: number, patch: Partial<EncounterPrescriptionItem>) {
    setRx((prev) => {
      const next = prev.map((it, x) => (x === i ? { ...it, ...patch } : it));
      saveRx.mutate(next);
      return next;
    });
  }
  /** Apply a whole toa mẫu — appends its items to the current prescription. */
  function applyTemplate(t: PrescriptionTemplateResponse) {
    const mapped: EncounterPrescriptionItem[] = t.items.map((ti) => ({
      id: randomUuid(),
      medicineId: ti.medicineId,
      medicineName: ti.medicineName,
      medicineUnit: ti.medicineUnit,
      medicineStrength: ti.medicineStrength,
      quantity: ti.quantity,
      doseMorning: ti.doseMorning,
      doseNoon: ti.doseNoon,
      doseAfternoon: ti.doseAfternoon,
      doseEvening: ti.doseEvening,
      doseUnit: ti.doseUnit ?? ti.medicineUnit,
      route: ti.route ?? "ORAL",
      mealTiming: ti.mealTiming,
      note: ti.note,
    }));
    const next = [...rx, ...mapped];
    setRx(next);
    saveRx.mutate(next);
  }

  // ---- sign / cancel ----
  const sign = useMutation({
    mutationFn: async () => {
      // #18535 — validate the prescription BEFORE signing. signEncounter locks the
      // encounter, so a drug with quantity <= 0 must be rejected here (not swallowed
      // by the best-effort createFromEncounter below, which runs after the lock).
      const badRx = firstRxQtyError();
      if (badRx) {
        throw new Error(`Vui lòng nhập số lượng (> 0) cho thuốc: ${badRx}.`);
      }
      await encountersApi.signEncounter(id);
      try {
        await prescriptionsApi.createFromEncounter(id);
      } catch {
        /* best-effort — prescription may be empty */
      }
      try {
        // #18527 — chốt chi phí: tự tạo hoá đơn thuốc + dịch vụ (mỗi loại 1 hoá đơn,
        // chỉ khi > 0đ). Phí khám giữ cơ chế riêng. Best-effort: không có khoản thu là
        // hợp lệ, không chặn việc ký.
        await billingApi.autoInvoicesFromEncounter(id);
      } catch {
        /* best-effort — không có khoản cần thu là bình thường */
      }
    },
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: encounterQueryKeys.queue() });
    },
    onError: (e2) => {
      const problem = apiError(e2);
      setErr(problem.detail);
      // #19810 — finished in another tab or on another machine: reload, so this screen turns into
      // the read-only signed record instead of offering "Hoàn tất" again.
      if (problem.code === "encounter_already_signed") {
        invalidate();
        qc.invalidateQueries({ queryKey: encounterQueryKeys.queue() });
      }
    },
  });
  const cancel = useMutation({
    mutationFn: (reason: string) => encountersApi.cancelEncounter(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: encounterQueryKeys.queue() });
      navigate("/encounters");
    },
    onError: (e2) => setErr(apiError(e2).detail),
  });


  // ---- A: Xuất hoá đơn từ lần khám — tách theo nguồn (thuốc / dịch vụ), > 0đ.
  // Dùng chung luồng auto với bước ký (#18527): idempotent, không tạo trùng.
  const makeInvoice = useMutation({
    mutationFn: () => billingApi.autoInvoicesFromEncounter(id),
    onSuccess: (list) => {
      if (list.length === 1) navigate(`/invoices/${list[0].id}`);
      else if (list.length > 1) navigate(`/invoices`);
      else setErr("Lần khám này chưa có khoản cần thu (thuốc/dịch vụ).");
    },
    onError: (e2) => setErr(apiError(e2).detail),
  });

  // ---- B: Tự tạo lịch tái khám khi có "Ngày hẹn kế tiếp" ----
  /** YYYY-MM-DD hợp lệ và ở tương lai (>= hôm nay). */
  function isFutureDate(iso: string): boolean {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
    return iso >= today;
  }
  /** Books the tái khám the doctor picked, if any. Soft-fails so it never blocks the signature. */
  async function createFollowUpAppointment() {
    await followUpRef.current?.createIfScheduled();
  }


  /**
   * #18535 — a drug row with a medicine chosen but quantity <= 0 must not reach the
   * sign/auto-invoice/print flow. Returns the offending drug name (for the message),
   * or null when every prescribed drug has a positive quantity. An empty prescription
   * (no drug rows) stays valid — a visit without medication is legitimate.
   */
  function firstRxQtyError(): string | null {
    const bad = rx.find((it) => it.medicineId && (!it.quantity || it.quantity <= 0));
    return bad ? bad.medicineName : null;
  }

  function finish() {
    setErr(null);
    if (dx.length === 0) {
      setDxError(true);
      dxSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const rxBad = firstRxQtyError();
    if (rxBad) {
      setRxError(rxBad);
      rxSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setRxError(null);
    // #19184 — no unanswered drug-allergy warning rides through a visit signature.
    withAllergyGate(() =>
      sign.mutate(undefined, {
        onSuccess: () => {
          if (isFutureDate(followUp)) createFollowUpAppointment();
        },
      })
    );
  }

  // ---- Thao tác hàng đợi của bác sĩ (design §7/§21). Chỉ khi lần khám gắn với 1 lịch hẹn. ----
  const apptId = detail.data?.appointmentId ?? null;
  // #18807: one owner for "a mutation is in flight on this appointment". Named for what it is —
  // the server owns every real queue invariant (callNext uses FOR UPDATE SKIP LOCKED), so this is
  // an affordance, not a business rule. See docs/architecture/queue-action-busy-ownership.md.
  const queueGuard = useAppointmentActionGuard(apptId);
  const toast = useToast();

  /**
   * Hoàn tất & gọi tiếp (§7 fast path) — sign the current encounter (clinical completion), then ask
   * the backend to call the next eligible patient into THIS room. Call-next runs as a separate
   * request AFTER the sign succeeds, so a failed/empty queue never affects the clinical completion
   * (§0.3). Empty queue → no number is invented (§8/case-1).
   *
   * <p><b>Then it leaves this page (#18602).</b> It used to stay put: the encounter went read-only,
   * a line of text said the next patient had been called, and the doctor was parked on a finished
   * visit with nothing to act on. The button says "gọi tiếp"; pressing it IS the statement that
   * this visit is over.
   *
   * <p>It goes to the LIST, not into the next patient's encounter, and that distinction is the
   * whole point. The reporter asked for both at different times — "mở thông tin khám của bệnh
   * nhân kế tiếp" first, then "không tự động chuyển sang đang khám khi chưa có thao tác bắt đầu".
   * Only one behaviour satisfies both: put the doctor where the called patient is visible and let
   * them press "Bắt đầu khám" deliberately. That also matches what the backend already enforces —
   * callNext only sets queue_state = CALLED and never opens an encounter.
   *
   * <p>The outcome goes through the app-level toast rather than {@code queueGuard.message},
   * because that message is state on THIS component and would be destroyed by the very navigation
   * this fix adds — taking "đã gọi STT 13" with it, the one fact the doctor carries forward.
   */
  function finishAndCallNext() {
    setErr(null);
    queueGuard.clear();
    if (dx.length === 0) {
      setDxError(true);
      dxSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const rxBad = firstRxQtyError();
    if (rxBad) {
      setRxError(rxBad);
      rxSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setRxError(null);
    // #19184 — no unanswered drug-allergy warning rides through a visit signature.
    withAllergyGate(() =>
      sign.mutate(undefined, {
        onSuccess: async () => {
          if (isFutureDate(followUp)) await createFollowUpAppointment();
          if (!apptId) return;
          try {
            // `called?.found`: a null response would throw here and be caught below as "không gọi
            // được", which is a false statement about a call that may well have succeeded.
            const called = await queueOpsApi.callNextForRoom(apptId);
            if (called?.found) {
              toast.success(
                `Đã gọi bệnh nhân kế tiếp (STT ${called.roomNo ?? called.registrationNo ?? "—"}).` +
                  " Bấm “Bắt đầu khám” khi sẵn sàng."
              );
            } else {
              toast.info("Đã hoàn tất. Hiện chưa có bệnh nhân chờ trong phòng.");
            }
          } catch (e2) {
            // The clinical completion already succeeded; say that first, because it is the part the
            // doctor must not be left doubting.
            toast.error(`Đã hoàn tất, nhưng không gọi được bệnh nhân kế: ${apiError(e2).detail}`);
          }
          // #18602 (retest 30/09) — làm mới danh sách TRƯỚC khi rời trang, và ĐỢI nó về.
          //
          // Khối "Đã check-in — chờ bắt đầu khám" của trang danh sách đọc key APPOINTMENTS
          // (`appointmentQueryKeys.forDate`), còn luồng ký chỉ vô hiệu hoá key ENCOUNTERS — nên bản
          // cũ sống nguyên và bệnh nhân vừa khám xong vẫn hiện ở đó kèm nút "Bắt đầu khám" bấm được.
          // QA bấm nút đó và hệ thống mở một lượt khám thứ hai cho lịch hẹn đã COMPLETED (3/3 lần).
          //
          // `await` chứ không bắn-rồi-quên: điều hướng trước khi dữ liệu về thì trang danh sách vẫn
          // vẽ bằng bản cũ đúng một nhịp — vừa đủ để bấm nhầm.
          await Promise.all([
            qc.invalidateQueries({ queryKey: appointmentQueryKeys.all }),
            qc.invalidateQueries({ queryKey: encounterQueryKeys.all }),
          ]);
          navigate("/encounters");
        },
      })
    );
  }

  /**
   * Flush save — everything already auto-saves, so this just forces the current
   * vitals + SOAP through and shows a brief "Đã lưu" confirmation toast.
   */
  function flushSave() {
    if (readOnly) return;
    if (vitals) saveVitals.mutate(vitals);
    if (soap)
      saveSoap.mutate({
        history: soap.history,
        exam: soap.exam,
        assessment: soap.assessment,
        updatedAt: soap.updatedAt,
      });
    setSavedToast(true);
    setTimeout(() => setSavedToast(false), 2000);
  }

  /**
   * Hoàn tất khám & In — sign the encounter, then open the print view. Reuses the
   * existing /encounters/:id/print route once signing succeeds.
   */
  function finishAndPrint() {
    setErr(null);
    if (dx.length === 0) {
      setDxError(true);
      dxSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const rxBad = firstRxQtyError();
    if (rxBad) {
      setRxError(rxBad);
      rxSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setRxError(null);
    // #19184 — no unanswered drug-allergy warning rides through a visit signature.
    withAllergyGate(() =>
      sign.mutate(undefined, {
        onSuccess: async () => {
          if (isFutureDate(followUp)) await createFollowUpAppointment();
          // Wait for the signed copy before leaving (#19171, L5): the print page reads this same
          // cache entry, and handing it the pre-sign copy is what printed "Kết thúc: —".
          await invalidate();
          navigate(`/encounters/${id}/print`);
        },
      })
    );
  }

  // F9 = Hoàn tất khám
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "F9" && !readOnly) {
        ev.preventDefault();
        finish();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]);

  if (!rawId) return <Navigate to="/encounters" replace />;

  // #18801 — the failure check comes BEFORE the loading check, and the order is the whole fix.
  //
  // `soap` and `vitals` are only ever assigned from a successful response, so when the request
  // fails they stay null. With the loading guard first, `!soap` kept winning and this branch was
  // unreachable: a doctor whose encounter failed to load saw a grey skeleton forever, identical
  // to a slow network, with nothing to read and nothing to press. Three states, three answers —
  // loading, loaded, failed — and the failed one offers a retry rather than a dead end.
  if (detail.isError) {
    return (
      <ErrorState
        title="Không tải được lần khám"
        error={detail.error}
        onRetry={() => {
          void detail.refetch();
        }}
      />
    );
  }
  if (detail.isLoading || !soap || !vitals) {
    return (
      <div className="grid gap-4" role="status" aria-busy="true" aria-label="Đang tải lần khám">
        <Skeleton height={120} />
        <Skeleton height={400} />
      </div>
    );
  }
  // Succeeded, but there is no encounter behind this id — a different thing from a failed request,
  // and it must not be reported as one.
  if (!detail.data) {
    return (
      <ErrorState
        title="Không tìm thấy lần khám"
        error={null}
        onRetry={() => {
          void detail.refetch();
        }}
      />
    );
  }
  const e = detail.data;
  // Chief complaint as chips (comma-separated) for the self-description input.
  const chiefChips = chief
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  // Best-effort "Phòng thực hiện": no service→room mapping exists, so we reuse
  // the service's category (khoa/nhóm) when we can match by name, else "—".
  // queue stats
  // Vitals out-of-range → red text/border ring.

  return (
    <div className="grid grid-cols-1 gap-4 pb-24 lg:grid-cols-[260px_minmax(0,1fr)_320px]">
      <EncounterQueueSidebar currentEncounterId={e.id} today={today} />

      {/* -------- CENTER: exam -------- */}
      <div className="order-1 flex flex-col gap-4 lg:order-2">
        <PatientContextPanel encounter={e} readOnly={readOnly} />

        {/* 1. Khám lâm sàng — vitals first (đo trước khi bác sĩ khám) */}
        <ExamSection title="1. Khám lâm sàng">
          <VitalsPanel vitals={vitals} onChange={patchVital} disabled={readOnly} />
          <div className="mt-3">
            <Textarea
              rows={2}
              disabled={readOnly}
              value={soap.exam.general}
              onChange={(ev) => patchSoap("exam", "general", ev.target.value)}
              placeholder="Khám khác: tim, phổi, bụng…"
            />
          </div>
        </ExamSection>

        {/* Bệnh nhân tự mô tả — chip input (triệu chứng), lưu vào chiefComplaint */}
        <ExamSection title="Bệnh nhân tự mô tả">
          <ChipSuggestInput
            chips={chiefChips}
            suggestions={symptomSuggestions}
            disabled={readOnly}
            placeholder="Nhập triệu chứng… (Enter để thêm)"
            onChange={(next) => {
              const joined = next.join(", ");
              setChief(joined);
              if (!readOnly) saveChief.mutate(joined);
            }}
          />
          <p className="mt-2 text-xs text-muted-foreground">
            Nguồn: {bookingSourceLabel(e.bookingSource)}
          </p>
        </ExamSection>

        {/* Chẩn đoán (ICD-10) — thêm mã ICD-10 hoặc chẩn đoán tự do */}
        <ExamSection title="Chẩn đoán">
          <DiagnosisPanel
            ref={dxSectionRef}
            diagnoses={dx}
            onChange={(next) => {
              setDx(next);
              debounce("dx", () => saveDx.mutate(next));
            }}
            readOnly={readOnly}
            error={dxError}
          />
        </ExamSection>

        {/* Chỉ định cận lâm sàng / dịch vụ (X-quang, siêu âm, xét nghiệm…) */}
        <ExamSection title="Chỉ định CLS / dịch vụ">
          <OrdersPanel
            orders={svcOrders}
            onChange={setServiceOrders}
            services={services.data ?? []}
            readOnly={readOnly}
            onPrintSlip={(serviceName) =>
              printServiceSlip({
                serviceName,
                patientName: e.patientName,
                patientCode: e.patientCode,
                patientAge: e.patientAge,
                patientGender: e.patientGender,
                doctorName: e.doctorName,
              })
            }
          />
        </ExamSection>

        {/* Sơ đồ răng — chỉ ngành nha khoa (#18868). Ghi nhận ngay trong lượt khám, nên mỗi
            ghi nhận mang theo encounterId làm bối cảnh lâm sàng; bản thân sơ đồ vẫn là dữ liệu
            theo BỆNH NHÂN, không thuộc về một lượt khám. */}
        {dentalEnabled && patientId && (
          <ExamSection title="Sơ đồ răng">
            <DentalChartPanel patientId={patientId} encounterId={e.id} />
          </ExamSection>
        )}

        {/* Kê đơn (thuốc / vật tư / dịch vụ / dinh dưỡng) — mục cuối cột giữa. Ẩn theo gói ×
            chế độ vận hành: Khám bệnh gói nào cũng có, Đơn thuốc thì không (ADR-0053). */}
        {prescribing.operates && (
          <ExamSection title="Kê đơn">
            <DrugAllergyStrip patientId={patientId} />
            <PrescriptionSection
              ref={rxSectionRef}
              items={rx}
              onPatch={patchRx}
              onPatchImmediate={setRxImmediate}
              onRemove={removeRx}
              onPickMedicine={pickMed}
              onApplyTemplate={applyTemplate}
              onOpenTemplateModal={() => setTplOpen(true)}
              onAppendPlan={(line) =>
                patchSoap(
                  "assessment",
                  "definitivePlan",
                  soap.assessment.definitivePlan
                    ? `${soap.assessment.definitivePlan}\n${line}`
                    : line
                )
              }
              error={err}
              qtyError={rxError}
              chiefChips={chiefChips}
              readOnly={readOnly}
              onError={setErr}
              allergyMarks={allergyMarks}
            />
          </ExamSection>
        )}
      </div>

      {/* -------- RIGHT: lịch sử · lời dặn · tổng kết -------- */}
      <div className="order-3 flex flex-col gap-4">
        <Card className="p-4">
          {(() => {
            // Chỉ hiện lần khám ĐÃ hoàn thành/đã ký — bỏ lần đang khám dở, mới mở, hoặc đã huỷ
            // (những lần khám "không diễn ra" không được tính vào lịch sử). Chỉ 5 lần gần nhất;
            // nhiều hơn thì "Xem thêm" mở trang lịch sử đầy đủ của bệnh nhân.
            const past = (history.data ?? []).filter(
              (h) =>
                h.id !== e.id && ["SIGNED", "COMPLETED"].includes(h.status as string),
            );
            const shown = past.slice(0, 5);
            return (
              <>
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Lịch sử khám
                  </h3>
                  {patientId && (
                    <Link
                      className="text-xs text-primary hover:underline"
                      to={`/patients/${patientId}/history`}
                    >
                      Xem lịch sử khám{past.length > 5 ? ` (${past.length})` : ""}
                    </Link>
                  )}
                </div>
                {history.isLoading ? (
                  <p className="text-sm text-muted-foreground">Đang tải…</p>
                ) : shown.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Chưa có lần khám trước.
                  </p>
                ) : (
                  <div className="space-y-1">
                    {shown.map((h) => (
                      <Link
                        key={h.id}
                        to={`/encounters/${h.id}`}
                        className="flex items-baseline gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
                      >
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {formatDateVn(h.startedAt)}
                        </span>
                        <span className="truncate">
                          {h.diagnosisSummary ||
                            h.chiefComplaint ||
                            "Không có chẩn đoán"}
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </Card>

        <Card className="p-4">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Lời dặn
          </h3>
          <QuickPicks
            picks={PICKS.advice}
            disabled={readOnly}
            onPick={(p) =>
              patchSoap(
                "assessment",
                "definitivePlan",
                appendPhrase(soap.assessment.definitivePlan, p, "\n")
              )
            }
          />
          <div className="mt-2">
            <Textarea
              rows={3}
              disabled={readOnly}
              value={soap.assessment.definitivePlan}
              onChange={(ev) =>
                patchSoap("assessment", "definitivePlan", ev.target.value)
              }
              placeholder="Dặn dò, hẹn tái khám, chế độ ăn…"
            />
          </div>
          <FollowUpPanel
            ref={followUpRef}
            encounterId={id}
            date={followUp}
            today={today}
            defaultDoctorId={e.doctorId ?? null}
            disabled={readOnly}
            onDateChange={(iso) => {
              setFollowUp(iso);
              // The date is mirrored into the plan text, which is why this page still owns it.
              const base = soap.assessment.definitivePlan
                .split("\n")
                .filter((l) => !l.trim().startsWith("Hẹn tái khám:"))
                .join("\n")
                .replace(/\n+$/, "");
              const next = iso
                ? (base ? base + "\n" : "") + "Hẹn tái khám: " + isoToVn(iso)
                : base;
              patchSoap("assessment", "definitivePlan", next);
            }}
          />
        </Card>

        <Card className="p-4">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Tổng kết
          </h3>
          <div className="flex items-center justify-between border-b py-1.5 text-sm">
            <span className="text-muted-foreground">Chẩn đoán</span>
            <span>{dx.find((d) => d.primary)?.label ?? `${dx.length}`}</span>
          </div>
          <div className="flex items-center justify-between border-b py-1.5 text-sm">
            <span className="text-muted-foreground">Số thuốc</span>
            <span>{rx.length}</span>
          </div>
          <div className="flex items-center justify-between border-b py-1.5 text-sm">
            <span className="text-muted-foreground">Chỉ định CLS</span>
            <span>{svcOrders.length}</span>
          </div>
          <div className="flex items-center justify-between py-1.5 text-sm font-medium">
            <span>Chi phí</span>
            <span className="text-xs font-medium text-muted-foreground">
              Tính ở bước Hoá đơn
            </span>
          </div>
        </Card>
      </div>

      {/* -------- BOTTOM: action bar -------- */}
      {/* Fixed bar starts after the sidebar width (--sw, set on the AppShell root) on
          desktop so it never underlaps the sidebar; full-width on mobile where the
          sidebar is an overlay drawer. Tracks collapse/resize reactively via --sw. */}
      <div className="fixed bottom-0 left-0 right-0 z-40 flex flex-wrap items-center gap-2 border-t bg-card/95 px-4 py-2.5 backdrop-blur supports-[backdrop-filter]:bg-card/80 md:left-[var(--sw)]">
        <Button asChild variant="ghost" size="sm">
          <Link to={`/encounters/${id}/attachments`}>
            <Paperclip className="mr-1.5 h-4 w-4" /> Đính kèm
          </Link>
        </Button>
        <Button asChild variant="ghost" size="sm">
          <Link to={`/encounters/${id}/print`}>
            <Printer className="mr-1.5 h-4 w-4" /> In phiếu
          </Link>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => makeInvoice.mutate()}
          disabled={makeInvoice.isPending}
          title="Tạo hoá đơn (chưa thanh toán) từ thuốc + chỉ định"
        >
          <ReceiptText className="mr-1.5 h-4 w-4" />
          {makeInvoice.isPending ? "Đang xuất…" : "Xuất hoá đơn"}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate("/encounters")}
        >
          <Pause className="mr-1.5 h-4 w-4" /> Tạm dừng
        </Button>
        {!readOnly && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => {
              const reason = window.prompt("Lý do huỷ khám?");
              if (reason) cancel.mutate(reason);
            }}
          >
            <Ban className="mr-1.5 h-4 w-4" /> Huỷ khám
          </Button>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* #19809 — the queue outcome lives IN the fixed action bar, beside the buttons that
              caused it. Rendered as a child of the page grid it took the first 260px cell and
              shifted all three columns one cell over. */}
          {queueGuard.message && (
            <p role="status" className="text-[13px] text-primary">
              {queueGuard.message}
            </p>
          )}
          {readOnly ? (
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setTransferOpen(true);
                }}
                title="Chuyển bệnh nhân sang khoa khác để khám tiếp"
              >
                <ArrowLeftRight className="mr-1.5 h-4 w-4" /> Chuyển khoa
              </Button>
              <Button asChild>
                <Link to={`/encounters/${id}/print`}>Xem / In phiếu khám →</Link>
              </Button>
            </>
          ) : (
            <>
              {apptId && (
                <QueueActions appointmentId={apptId} guard={queueGuard} />
              )}
              <Button
                type="button"
                variant="secondary"
                onClick={flushSave}
                title="Lưu tất cả (mọi thay đổi đã tự động lưu)"
              >
                {savedToast ? "Đã lưu ✓" : "Lưu"}
              </Button>
              {apptId && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={finishAndCallNext}
                  disabled={sign.isPending || queueGuard.busy}
                  title="Ký lần khám rồi gọi bệnh nhân kế tiếp vào phòng"
                >
                  {sign.isPending ? "Đang hoàn tất…" : "Hoàn tất & gọi tiếp"}
                </Button>
              )}
              <Button
                type="button"
                onClick={finishAndPrint}
                disabled={sign.isPending}
              >
                {sign.isPending ? "Đang hoàn tất…" : "Hoàn tất khám & In"}
                <small className="ml-1.5 rounded bg-primary-foreground/20 px-1 text-[10px]">
                  F9
                </small>
              </Button>
            </>
          )}
        </div>
      </div>

      <TemplatePickerModal
        open={tplOpen}
        onClose={() => setTplOpen(false)}
        onPick={applyTemplate}
      />

      {/* #19184 — answering an allergy warning before the visit is signed. */}
      <DrugAllergyWarningDialog
        open={signGate !== null}
        medicineName={signGate?.queue[0]?.medicineName ?? ""}
        warnings={signGate?.queue[0]?.warnings ?? []}
        onCancel={() => {
          setSignGate(null);
          setErr(
            "Chưa hoàn tất khám: đơn thuốc có thuốc bệnh nhân đang dị ứng. Bỏ thuốc đó khỏi đơn, hoặc xác nhận và nêu lý do."
          );
        }}
        onConfirm={(reasonCode, reasonText) => void answerSignGate(reasonCode, reasonText)}
      />

      <DrugAllergyWarningDialog

        open={allergyPrompt !== null}

        medicineName={allergyPrompt?.med.name ?? ""}

        warnings={allergyPrompt?.warnings ?? []}

        onCancel={() => setAllergyPrompt(null)}

        onConfirm={(reasonCode: AllergyOverrideReason, reasonText: string | null) => {

          const p = allergyPrompt;

          setAllergyPrompt(null);

          if (!p) return;

          // Record one override per matched allergy — the guard at finalize checks each pair, so
          // a single row would leave the prescription unfinishable for the others.

          if (patientId) {

            for (const w of p.warnings) {

              patientsApi

                .overrideDrugAllergy(patientId, {

                  medicineId: p.med.id,

                  allergyId: w.allergyId,

                  encounterId: id,

                  reasonCode,

                  reasonText,

                })

                .catch(() => {});

            }

          }

          addMed(p.med);

        }}

      />

      <TransferDepartmentDialog
        encounterId={id}
        open={transferOpen}
        onOpenChange={setTransferOpen}
        onError={setErr}
      />
    </div>
  );
}
